import { useEffect, useState } from "react";
import { X, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useAuthModal } from "../context/AuthModalContext";
import Input from "./ui/Input";
import Button from "./ui/Button";
import SocialAuthButtons from "./SocialAuthButtons";
import PhoneInput from "./PhoneInput";
import TermsCheckbox from "./TermsCheckbox";
import { apiErrorMessage } from "../lib/utils";

export default function AuthModal() {
  const { mode, closeAuthModal, openAuthModal } = useAuthModal();
  const { login, requestSignupCode, register, verify2fa } = useAuth();
  const isOpen = mode !== null;

  const [step, setStep] = useState("email"); // "email" | "login" | "register" | "signup-code" | "2fa"
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [twoFactor, setTwoFactor] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep("email");
    setPassword("");
    setError(null);
  }, [isOpen, mode]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && closeAuthModal();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, closeAuthModal]);

  if (!isOpen) return null;

  const onContinue = (e) => {
    e.preventDefault();
    if (!email) return;
    setError(null);
    setStep(mode === "register" ? "register" : "login");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (step === "register") {
        // No account yet: we email a code first, and create it once it is entered.
        setSentTo(await requestSignupCode({ full_name: fullName, email, phone, password, accept_terms: acceptTerms }));
        setCode("");
        setStep("signup-code");
        return;
      } else {
        const result = await login({ email, password });
        if (result?.requires2fa) {
          setTwoFactor(result);
          setCode("");
          setStep("2fa");
          return;
        }
      }
      closeAuthModal();
    } catch (err) {
      setError(apiErrorMessage(err, step === "register" ? "Could not send your code." : "Invalid credentials."));
    } finally {
      setLoading(false);
    }
  };

  const onCreateAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await register({ full_name: fullName, email, phone, password, accept_terms: acceptTerms, code });
      closeAuthModal();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not create your account."));
    } finally {
      setLoading(false);
    }
  };

  const onVerify2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await verify2fa({ phone: twoFactor.phone, email: twoFactor.email, code });
      closeAuthModal();
    } catch (err) {
      setError(err?.response?.data?.message || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onMouseDown={(e) => e.target === e.currentTarget && closeAuthModal()}
    >
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <button
          type="button"
          onClick={closeAuthModal}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-full p-1 text-slate-500 hover:bg-slate-100"
        >
          <X className="h-5 w-5" />
        </button>

        {step === "email" && (
          <>
            <h2 className="mb-2 text-center font-montserrat text-2xl font-bold text-slate-900">
              Get started
            </h2>
            <p className="mb-6 text-center text-sm text-muted-foreground">
              Create an account or log in to book and manage your appointments.
            </p>
            <form onSubmit={onContinue} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm text-slate-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" disabled={!email}>
                Continue
              </Button>
            </form>

            <div className="relative my-6 flex items-center">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="px-3 text-xs uppercase text-muted-foreground">or</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            <SocialAuthButtons onError={setError} onSuccess={closeAuthModal} />

            {error && <p className="mt-4 text-center text-sm text-destructive">{error}</p>}

            <p className="mt-6 text-center text-sm text-muted-foreground">
              {mode === "register" ? (
                <>
                  Already have an account?{" "}
                  <button
                    type="button"
                    className="font-medium text-primary"
                    onClick={() => openAuthModal("login")}
                  >
                    Log in
                  </button>
                </>
              ) : (
                <>
                  No account?{" "}
                  <button
                    type="button"
                    className="font-medium text-primary"
                    onClick={() => openAuthModal("register")}
                  >
                    Sign up
                  </button>
                </>
              )}
            </p>
          </>
        )}

        {(step === "login" || step === "register") && (
          <>
            <button
              type="button"
              onClick={() => setStep("email")}
              className="mb-2 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <h2 className="mb-1 text-center font-montserrat text-2xl font-bold text-slate-900">
              {step === "register" ? "Create your account" : "Enter your password"}
            </h2>
            <p className="mb-6 text-center text-sm text-muted-foreground">{email}</p>
            <form onSubmit={onSubmit} className="space-y-4">
              {step === "register" && (
                <Input
                  placeholder="Full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  autoFocus
                />
              )}
              {step === "register" && <PhoneInput value={phone} onChange={setPhone} aria-label="Phone number" required />}
              <Input
                type="password"
                placeholder={step === "register" ? "Password (min 8 characters)" : "Password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus={step === "login"}
              />
              {step === "register" && <TermsCheckbox checked={acceptTerms} onChange={setAcceptTerms} />}
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading || (step === "register" && !acceptTerms)}>
                {loading
                  ? step === "register"
                    ? "Sending your code…"
                    : "Logging in…"
                  : step === "register"
                  ? "Sign up"
                  : "Log in"}
              </Button>
            </form>
          </>
        )}

        {step === "signup-code" && (
          <>
            <button
              type="button"
              onClick={() => setStep("register")}
              className="mb-2 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <h2 className="mb-1 text-center font-montserrat text-2xl font-bold text-slate-900">Confirm your email</h2>
            <p className="mb-6 text-center text-sm text-muted-foreground">
              Enter the 6-digit code we sent to {sentTo}. Your account is created once you do.
            </p>
            <form onSubmit={onCreateAccount} className="space-y-4">
              <Input
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-label="Verification code"
                required
                autoFocus
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
                {loading ? "Creating account…" : "Create my account"}
              </Button>
            </form>
          </>
        )}

        {step === "2fa" && (
          <>
            <button
              type="button"
              onClick={() => setStep("login")}
              className="mb-2 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <h2 className="mb-1 text-center font-montserrat text-2xl font-bold text-slate-900">
              Two-factor verification
            </h2>
            <p className="mb-6 text-center text-sm text-muted-foreground">
              Enter the 6-digit code we sent to {twoFactor?.sentTo || twoFactor?.phone || twoFactor?.email}.
            </p>
            <form onSubmit={onVerify2fa} className="space-y-4">
              <Input
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                autoFocus
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
                {loading ? "Verifying…" : "Verify"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
